import React from "react";

const getAllBlanksInQuestion = (question) =>
  question?.containers?.flatMap((container) =>
    [...(container.blanks || [])].sort((a, b) => a.index - b.index),
  ) || [];

const extractAnswerValue = (val, matchedBlankId) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "object") {
    if (matchedBlankId) {
      if (val.itemId === matchedBlankId) return val.targetId;
    }
    return (
      val.targetId ??
      val.optionId ??
      val.selectedOptionId ??
      val.itemId ??
      val.id ??
      val._id ??
      val.value ??
      val.text ??
      val.answer ??
      val.content ??
      null
    );
  }
  return val;
};

const getAnswerData = (questionAnswer) => {
  if (!questionAnswer) return null;
  return (
    questionAnswer.answer ??
    questionAnswer.userAnswer ??
    questionAnswer.studentAnswer ??
    questionAnswer.answers ??
    questionAnswer.value ??
    questionAnswer
  );
};

const getStudentAnswerForBlank = (
  blank,
  container,
  containerIdx,
  questionAnswer,
  allBlanks,
) => {
  const ansData = getAnswerData(questionAnswer);
  if (ansData === null || ansData === undefined) return null;

  const blankId = blank?.id ?? blank?._id;
  const containerId = container?.id ?? container?._id;

  const globalBlankIdx = allBlanks.findIndex(
    (b) => b === blank || ((b.id ?? b._id) && (b.id ?? b._id) === blankId),
  );

  if (Array.isArray(ansData)) {
    const match = ansData.find(
      (item) => item && blankId && item.targetId === blankId,
    );
    if (match !== undefined) {
      const extracted = extractAnswerValue(match, blankId);
      if (extracted !== null) return extracted;
    }

    const indicesToCheck = [globalBlankIdx, containerIdx];
    for (const idx of indicesToCheck) {
      if (idx !== undefined && idx !== -1 && idx < ansData.length) {
        const extracted = extractAnswerValue(ansData[idx], blankId);
        if (extracted !== null) return extracted;
      }
    }
  } else if (typeof ansData === "object") {
    const possibleKeys = [
      blankId,
      containerId,
      globalBlankIdx !== -1 ? globalBlankIdx : null,
      containerIdx,
      blank.index,
    ];

    for (const key of possibleKeys) {
      if (key !== null && key !== undefined) {
        const val = ansData[key] ?? ansData[String(key)];
        if (val !== undefined && val !== null) {
          const extracted = extractAnswerValue(val, blankId);
          if (extracted !== null) return extracted;
        }
      }
    }
  } else if (
    (typeof ansData === "string" || typeof ansData === "number") &&
    allBlanks.length === 1
  ) {
    return ansData;
  }

  return null;
};

const BlankBadge = ({
  blank,
  container,
  containerIdx,
  questionAnswer,
  options,
  allBlanks,
}) => {
  const studentAnswerVal = getStudentAnswerForBlank(
    blank,
    container,
    containerIdx,
    questionAnswer,
    allBlanks,
  );

  const selectedOption = options?.find((opt) => {
    const optId = opt.id;
    return (
      optId === studentAnswerVal || String(optId) === String(studentAnswerVal)
    );
  });

  const studentAnswerText =
    selectedOption?.text ??
    (studentAnswerVal !== null && studentAnswerVal !== undefined
      ? String(studentAnswerVal)
      : null);

  const correctOption = options?.find((opt) => {
    const optId = opt.id;
    const targetId = blank.optionId;
    return optId === targetId;
  });

  const correctAnswerText = blank.content || correctOption?.text || "";

  const isCorrect = selectedOption
    ? selectedOption.text?.trim() === correctAnswerText?.trim()
    : Boolean(
        studentAnswerText &&
        correctOption &&
        studentAnswerText.trim() === String(correctOption.id).trim(),
      );

  const hasAnswer = Boolean(
    studentAnswerText && studentAnswerText.trim() !== "",
  );
  const isGood = hasAnswer && isCorrect;

  return (
    <span className="d-inline-flex flex-column align-items-center mx-1 my-1">
      <span
        className="d-inline-flex align-items-center justify-content-center px-3 py-1 rounded gap-1"
        style={{
          minWidth: "120px",
          height: "36px",
          border: `2px ${hasAnswer ? "solid" : "dashed"} ${
            isGood ? "#8dc63f" : "#ef4444"
          }`,
          backgroundColor: isGood ? "#f0fdf4" : "#fef2f2",
          color: isGood ? "#15803d" : "#dc2626",
          fontWeight: "600",
          fontSize: "15px",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
          transition: "all 0.2s ease",
        }}
      >
        {isGood ? (
          <>
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>✓</span>
            <span>{studentAnswerText}</span>
          </>
        ) : hasAnswer ? (
          <>
            <span style={{ fontSize: "13px", fontWeight: "bold" }}>✕</span>
            <span>{studentAnswerText}</span>
          </>
        ) : (
          <span
            style={{ fontSize: "13px", color: "#ef4444", fontWeight: "500" }}
          >
            لم تتم الإجابة
          </span>
        )}
      </span>
      {!isGood && correctAnswerText && (
        <span
          className="badge mt-1"
          style={{
            backgroundColor: "#8dc63f",
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: "500",
            borderRadius: "4px",
            padding: "2px 6px",
          }}
        >
          الصحيح: {correctAnswerText}
        </span>
      )}
    </span>
  );
};

const FillBlanks = ({ question, activeQuestionAnswer }) => {
  const allBlanks = getAllBlanksInQuestion(question);

  return (
    <div
      className="fill-blanks-container mt-3"
      style={{ direction: "rtl", fontFamily: "inherit" }}
    >
      <div className="d-flex flex-column gap-3 text-end">
        {question?.containers?.map((container, idx) => (
          <div
            key={container.id}
            className="d-flex align-items-start gap-3 p-3 rounded-3"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center bg-light text-secondary rounded-circle fw-bold"
              style={{
                width: "28px",
                height: "28px",
                fontSize: "14px",
                flexShrink: 0,
                marginTop: "4px",
              }}
            >
              {idx + 1}
            </div>

            <div
              className="flex-grow-1 d-flex flex-wrap align-items-center gap-1 lh-lg"
              dir="rtl"
              style={{
                textAlign: "right",
                fontSize: "16px",
                color: "#4a4a4a",
                fontWeight: "400",
              }}
            >
              {(container.content?.split(/\s+/) || []).map((word, index) => {
                const blank = container.blanks?.find((b) => b.index === index);

                if (blank) {
                  return (
                    <BlankBadge
                      key={blank.id}
                      blank={blank}
                      container={container}
                      containerIdx={idx}
                      questionAnswer={activeQuestionAnswer}
                      options={question.options}
                      allBlanks={allBlanks}
                    />
                  );
                }

                return (
                  <span key={index} style={{ padding: "0 2px" }}>
                    {word}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FillBlanks;
